import { Text } from '@/components/gluestack/text';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import { Card } from '~/components/gluestack/card';
import { Skeleton } from '@/components/gluestack/skeleton';
import { Post, QuotaTypes } from '@/models/Post';
import { Photo } from '@/models/Photo';
import { RenderHtml } from '@/components/ui/RenderHtml';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { Image } from 'expo-image';
import { ProfilePicture } from '@/components/domain/common/ProfilePicture';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { useTranslation } from 'react-i18next';
import { LikeButton } from '@/components/domain/common/LikeButton';
import React, { useCallback, useMemo, useState, RefObject } from 'react';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { CommentCard } from '@/components/domain/comments/CommentCard';
import { CommentsSheet } from '@/components/domain/comments/CommentsSheet';
import { HStack } from '@/components/gluestack/hstack';
import { VStack } from '@/components/gluestack/vstack';
import { ActionLink } from '@/components/ui/ActionLink';
import { MemberListSheet } from '@/components/domain/common/MemberListSheet';
import { FlashList, useRecyclingState } from '@shopify/flash-list';
import { PostContextMenu } from './PostContextMenu';
import { ImageViewerTrigger } from '@/components/ui/image-viewer/ImageViewerTrigger';
import { useSystemTextTranslation } from '@/hooks/useSystemTextTranslation';
import { Link } from 'expo-router';
import { TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { useFormatCount } from '@/hooks/useFormatCount';
import { getMemberPath } from '@/constants/routes';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { Mic01Icon, ChatIcon } from '@hugeicons-pro/core-stroke-rounded';
import { ProtectedImageContainer } from '@/components/ui/ProtectedImageContainer';
import { createLruCache } from '@/services/LruCacheService';

const MAX_IMAGE_ASPECT_RATIO_CACHE_ENTRIES = 128;
const imageAspectRatioCache = createLruCache<string, number>({
  maxEntries: MAX_IMAGE_ASPECT_RATIO_CACHE_ENTRIES,
});

const setCachedImageAspectRatio = (uri: string | null | undefined, ratio: number): void => {
  if (!uri || !ratio) return;

  imageAspectRatioCache.set(uri, ratio);
};

interface PostCardProps {
  post?: Post;
  flashListRef?: RefObject<React.ComponentRef<typeof FlashList<any>> | null>;
  itemIndex?: number;
  // Receives the post id so list screens can pass one stable handler to every card.
  onPostDeleted?: (postId: number) => void;
  onBeforeProgrammaticScroll?: () => void;
  noTruncate?: boolean;
  hideRecentComments?: boolean;
  hideActions?: boolean;
  disableImageViewer?: boolean;
  compactImage?: boolean;
  className?: string;
}

type PostCardContentProps = Omit<PostCardProps, 'post'> & {
  post: Post;
};

function PostCardComponent({ post, ...props }: PostCardProps) {
  if (!post) {
    return <PostCardSkeleton />;
  }

  return <PostCardContent post={post} {...props} />;
}

function PostCardContent({
  post,
  flashListRef,
  itemIndex,
  onPostDeleted,
  onBeforeProgrammaticScroll,
  noTruncate = false,
  hideRecentComments = false,
  hideActions = false,
  disableImageViewer = false,
  compactImage = false,
  className,
}: PostCardContentProps) {
  const isLargeScreen = useIsLargeScreen();
  const { height: windowHeight } = useWindowDimensions();
  const { t } = useTranslation();
  const [showCommentsSheet, setShowCommentsSheet] = useState(false);
  const [showLikesSheet, setShowLikesSheet] = useState(false);
  const [imageAspectRatio, setImageAspectRatio] = useRecyclingState(
    () => (post.PhotoUrl ? imageAspectRatioCache.get(post.PhotoUrl) : undefined) ?? 1,
    [post.PhotoUrl]
  );
  const { formatCount } = useFormatCount();

  const isOverQuota = post.OverQuota === QuotaTypes.PhotoStream;
  const useCompactImage = isOverQuota || compactImage;
  const systemText = useSystemTextTranslation(post);

  const compactViewerPhoto = useMemo(
    (): Photo =>
      ({
        Id: post.PhotoId ?? 0,
        IconUrl: post.PhotoIconUrl ?? '',
        DatePosted: post.DateCreated,
        CommentsCount: post.CommentsCount,
        LikesCount: post.LikesCount,
        LikedByViewer: post.IsLiked,
        Member: post.Member,
        IsLocked: isOverQuota,
      }) as Photo,
    [
      post.PhotoId,
      post.PhotoIconUrl,
      post.DateCreated,
      post.CommentsCount,
      post.LikesCount,
      post.IsLiked,
      post.Member,
      isOverQuota,
    ]
  );

  // Determine item type for sheets
  const likesItemType = post.IsPhoto ? t('common.photo') : t('common.post');
  const commentsItemType = post.IsPhoto ? t('common.photo') : t('common.post'); // Assuming comments also apply to the photo if PhotoId exists

  const handleShowLikes = useCallback(() => {
    if (!isLargeScreen && flashListRef?.current && itemIndex !== undefined) {
      onBeforeProgrammaticScroll?.();
      flashListRef.current?.scrollToIndex({ index: itemIndex, animated: true, viewPosition: 0 });
      setShowLikesSheet(true);
    } else {
      setShowLikesSheet(true);
    }
  }, [flashListRef, isLargeScreen, itemIndex, onBeforeProgrammaticScroll]);

  const handleShowComments = useCallback(() => {
    const hasComments = (post.CommentsCount ?? 0) > 0;
    if (!isLargeScreen && hasComments && flashListRef?.current && itemIndex !== undefined) {
      onBeforeProgrammaticScroll?.();
      flashListRef.current?.scrollToIndex({ index: itemIndex, animated: true, viewPosition: 0 });
    }
    setShowCommentsSheet(true);
  }, [flashListRef, isLargeScreen, itemIndex, onBeforeProgrammaticScroll, post.CommentsCount]);

  return (
    <>
      <Card size="md" className={className}>
        <HStack>
          <HStack className="flex-1 items-center">
            <ProfilePicture member={post.Member} showTooltip />

            <VStack className="ml-4 flex-1 gap-1 web:gap-0.5">
              <Link href={getMemberPath(post.Member)} asChild>
                <TouchableOpacity activeOpacity={0.8} className="self-start">
                  <Text bold className="web:hover:underline">
                    {post.Member.UserName}
                  </Text>
                </TouchableOpacity>
              </Link>
              <HStack className="items-center web:mt-0.5">
                <RelativeTime date={post.DateCreated} className="text-sm text-typography-500" />
                {post.IsSticky && (
                  <Badge className="-mb-0.5 ml-2 rounded-xl" variant="solid" action="warning">
                    <HStack className="items-center gap-1">
                      <HugeIconsWrapper icon={Mic01Icon} size={14} className="text-warning-600" />
                      <BadgeText>{t('post.announcement', 'Announcement')}</BadgeText>
                    </HStack>
                  </Badge>
                )}
                {post.IsEdited && (
                  <Badge className="-mb-0.5 ml-2 rounded-xl" variant="solid">
                    <BadgeText>{t('common.edited')}</BadgeText>
                  </Badge>
                )}
              </HStack>
            </VStack>
          </HStack>

          {/* Menu Button */}
          {!hideActions && <PostContextMenu post={post} onPostDeleted={onPostDeleted} />}
        </HStack>

        {/* User-Generated Content */}
        {post.HasText && !post.IsSystem && (
          <VStack className="mt-2">
            <RenderHtml
              html={post.TextNotEdited}
              detectHttpsLinks
              detectHashtags
              className={`${post.IsShortPost ? 'text-xl' : ''}`}
              noTruncate={noTruncate}
              showYouTube
              mediaClassName="mt-4 max-w-lg"
            />
          </VStack>
        )}

        {/* System-Generated Content */}
        {post.IsSystem &&
          systemText &&
          (post.PostType === 'NewEvent' && post.Event ? (
            <HStack className="mt-2 flex-wrap">
              <Text className="italic">{systemText}</Text>
              <Link href={`/events/${post.Event.Code}`} asChild>
                <TouchableOpacity activeOpacity={0.8} className="ml-1 mt-0.5 self-start web:mt-0">
                  <Text className="font-bold italic text-primary-500 web:hover:underline">
                    {post.Event.Name}
                  </Text>
                </TouchableOpacity>
              </Link>
            </HStack>
          ) : (
            <VStack className="mt-2">
              <RenderHtml html={systemText} detectHttpsLinks className="italic" noTruncate />
            </VStack>
          ))}

        {/* Post Image */}
        {post.IsPhoto &&
          (useCompactImage ? (
            <HStack className={`mt-4 items-center gap-4 ${isLargeScreen ? '' : 'mb-1'}`}>
              {disableImageViewer ? (
                <ProtectedImageContainer>
                  <Image
                    source={{ uri: post.PhotoIconUrl ?? undefined }}
                    className="h-32 w-32 rounded-lg"
                    contentFit="cover"
                    recyclingKey={post.PhotoIconUrl}
                  />
                </ProtectedImageContainer>
              ) : (
                <ImageViewerTrigger
                  uri={post.PhotoIconUrl ?? ''}
                  allowImageDownload={!!post.Member?.IsEvent}
                  photo={compactViewerPhoto}>
                  <ProtectedImageContainer>
                    <Image
                      source={{ uri: post.PhotoIconUrl ?? undefined }}
                      className="h-32 w-32 rounded-lg"
                      contentFit="cover"
                      recyclingKey={post.PhotoIconUrl}
                    />
                  </ProtectedImageContainer>
                </ImageViewerTrigger>
              )}
            </HStack>
          ) : (
            <VStack className={`mt-4 ${isLargeScreen ? '' : '-mx-7 -ml-[1.751rem] mb-1'}`}>
              {disableImageViewer ? (
                <ProtectedImageContainer
                  className={`overflow-hidden rounded-2xl ${isLargeScreen ? 'self-start' : ''}`}
                  style={{
                    width: isLargeScreen ? 512 : '100%',
                    maxWidth: '100%',
                    maxHeight: isLargeScreen ? windowHeight : undefined,
                    aspectRatio: imageAspectRatio,
                  }}>
                  <Image
                    source={{ uri: post.PhotoUrl ?? undefined }}
                    style={{ width: '100%', height: '100%' }}
                    contentFit="cover"
                    recyclingKey={post.PhotoUrl}
                    onLoad={(e) => {
                      const { width, height } = e.source;
                      if (!width || !height || !post.PhotoUrl) return;
                      // Cap portrait aspect ratio at 3:4 (height = 4/3 of width)
                      const ratio = Math.max(width / height, 3 / 4);
                      setImageAspectRatio(ratio);
                      setCachedImageAspectRatio(post.PhotoUrl, ratio);
                    }}
                  />
                </ProtectedImageContainer>
              ) : (
                <ImageViewerTrigger
                  uri={post.PhotoUrl ?? ''}
                  photoId={post.PhotoId ?? undefined}
                  allowImageDownload={!!post.Member?.IsEvent}
                  className={isLargeScreen ? 'self-start' : undefined}>
                  <ProtectedImageContainer
                    className="overflow-hidden rounded-2xl"
                    style={{
                      width: isLargeScreen ? 512 : '100%',
                      maxWidth: '100%',
                      maxHeight: isLargeScreen ? windowHeight : undefined,
                      aspectRatio: imageAspectRatio,
                    }}>
                    <Image
                      source={{ uri: post.PhotoUrl ?? undefined }}
                      style={{ width: '100%', height: '100%' }}
                      contentFit="cover"
                      recyclingKey={post.PhotoUrl}
                      onLoad={(e) => {
                        const { width, height } = e.source;
                        if (!width || !height || !post.PhotoUrl) return;
                        // Cap portrait aspect ratio at 3:4 (height = 4/3 of width)
                        const ratio = Math.max(width / height, 3 / 4);
                        setImageAspectRatio(ratio);
                        setCachedImageAspectRatio(post.PhotoUrl, ratio);
                      }}
                    />
                  </ProtectedImageContainer>
                </ImageViewerTrigger>
              )}
            </VStack>
          ))}

        {!hideActions && (
          <HStack className="mt-2 items-center gap-1">
            <LikeButton item={post} itemType="post" />
            {post.LikesCount > 0 && (
              <>
                <ActionLink onPress={handleShowLikes}>
                  {t(`post.like`, { count: post.LikesCount }).replace(
                    post.LikesCount.toString(),
                    formatCount(post.LikesCount)
                  )}
                </ActionLink>
                <ContextSheet
                  key={`likes-sheet-${post.Id}`}
                  isOpen={showLikesSheet}
                  onClose={() => setShowLikesSheet(false)}
                  contentComponent={MemberListSheet}
                  shortTitle={t('likes.title')}
                  title={t('likes.titleOnItem', {
                    itemType: likesItemType,
                    userName: post.Member.UserName,
                  })}
                  payload={{
                    listType: 'post-likes',
                    post,
                  }}
                />
              </>
            )}

            {/* Comment ActionLink (hidden for sticky posts) */}
            {!post.IsSticky && (
              <ActionLink onPress={handleShowComments}>
                {post.CommentsCount > 0
                  ? t('post.comment', { count: post.CommentsCount }).replace(
                      post.CommentsCount.toString(),
                      formatCount(post.CommentsCount)
                    )
                  : t('post.addComment')}
              </ActionLink>
            )}
            <ContextSheet
              key={`comments-sheet-${post.Id}`}
              isOpen={showCommentsSheet}
              onClose={() => setShowCommentsSheet(false)}
              contentComponent={CommentsSheet}
              shortTitle={t('comments.title')}
              title={t('comments.titleOnItem', {
                itemType: commentsItemType,
                userName: post.Member.UserName,
              })}
              expectsKeyboard={post.CommentsCount === 0}
              payload={{ post }}
            />
          </HStack>
        )}

        {!hideRecentComments &&
          post.RecentComments &&
          post.RecentComments.length > 0 &&
          (() => {
            const hasViewMore = post.CommentsCount > post.RecentComments.length;
            const rows: React.ReactNode[] = [];
            if (hasViewMore) {
              rows.push(
                <TouchableOpacity
                  key="view-more"
                  activeOpacity={0.7}
                  onPress={handleShowComments}
                  className="mb-1.5 self-stretch">
                  <HStack className="mb-1 min-h-8 items-center gap-3">
                    <View className="h-8 w-8 items-center justify-center rounded-full bg-background-100">
                      <HugeIconsWrapper icon={ChatIcon} size={16} className="text-typography-500" />
                    </View>
                    <Text size="sm" className="min-w-0 flex-1 text-typography-500 web:hover:underline">
                      {t('comments.viewMore', {
                        count: post.CommentsCount - post.RecentComments.length,
                      })}
                    </Text>
                  </HStack>
                </TouchableOpacity>
              );
            }
            post.RecentComments.forEach((comment) => {
              rows.push(
                <CommentCard
                  key={comment.Id}
                  comment={comment}
                  parentPost={post}
                  compact
                  onPress={handleShowComments}
                />
              );
            });
            const lastIndex = rows.length - 1;
            return (
              <VStack className="-mb-2 mt-2 pt-2">
                {rows.map((row, index) => {
                  const isLast = index === lastIndex;
                  return (
                    <View key={(row as React.ReactElement).key ?? index} className="relative pl-6">
                      <View
                        pointerEvents="none"
                        className="absolute left-2.5 top-0 h-4 w-3 rounded-bl-lg border-b border-l border-outline-200"
                      />
                      {!isLast && (
                        <View
                          pointerEvents="none"
                          className="absolute bottom-0 left-2.5 top-4 border-l border-outline-200"
                        />
                      )}
                      {row}
                    </View>
                  );
                })}
              </VStack>
            );
          })()}
      </Card>
    </>
  );
}

function PostCardSkeleton() {
  return (
    <Card size="md">
      <HStack className="items-center">
        <Skeleton className="h-12 w-12 rounded-full" />

        <VStack className="ml-4 flex-1 gap-2">
          <Skeleton className="h-5 w-36 rounded-md" />
          <Skeleton className="h-4 w-24 rounded-md" />
        </VStack>
      </HStack>

      {/* Post Content Skeleton */}
      <VStack className="py-4">
        <Skeleton className="mb-2 h-4 w-full rounded-md" />
        <Skeleton className="mb-2 h-4 w-full rounded-md" />
        <Skeleton className="h-4 w-3/4 rounded-md" />
      </VStack>
    </Card>
  );
}

export const PostCard = React.memo(PostCardComponent);
